import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import FB from './img/fb.webp';
import INSTA from './img/insta.webp';
import SC from './img/sc.webp';
import NotificationBadge from '../../../../components/NotificationBadge/NotificationBadge';
import QM from './img/questionMark.png';
import { FaWhatsapp, FaRobot, FaChevronDown } from 'react-icons/fa';
import { SiCalendly } from 'react-icons/si';
import { AIChatWidget } from '../../../../components/AIChat/AIChatWidget';

import css from './TopbarDesktop.module.css';

const DashboardLink = () => {
  return (
    <NamedLink name="DashboardPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.dashboardLink" />
      </span>
    </NamedLink>
  );
};

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, chatNotificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const chatDot = chatNotificationCount > 0 ? <NotificationBadge count={''} /> : null;
  return (
    <NamedLink
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
        {chatDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const isAdmin = currentUser?.attributes?.profile?.publicData?.isAdmin;
  const isProvider = currentUser?.attributes?.profile?.publicData?.userType === 'owner';

  if(isProvider) {
    return (
      <Menu>
        <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        </MenuLabel>
        <MenuContent className={css.profileMenuContent}>
          <MenuItem key="WishlistPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('WishlistPage'))}
              name="WishlistPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.wishlistPageLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
              params={{ tab: 'other' }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
          {currentUser?.attributes?.profile?.publicData?.userType == 'owner' && (
            <MenuItem key="ManageBookingsPage">
              <NamedLink
                className={classNames(css.menuLink, currentPageClass('InboxPage'))}
                name="InboxPage"
                params={{ tab: 'sales' }}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.manageBookingsLink" />
              </NamedLink>
            </MenuItem>
          )}
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="ProviderEscalationPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProviderEscalationPage'))}
              name="ProviderEscalationPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.customerQuestionsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
    );}else{
    return (
      <Menu>
        <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        </MenuLabel>
        <MenuContent className={css.profileMenuContent}>
          <MenuItem key="WishlistPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('WishlistPage'))}
              name="WishlistPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.wishlistPageLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
    )
  }
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {boolean} props.currentUserHasListings
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    isLandingPage,
    isListingPage = false,
    currentListing = null,
    listingReviews = [],
  } = props;

  const [supportOpen, setSupportOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const supportRef = useRef(null);

  const WA_URL =
    'https://wa.me/447501526140?text=Hi%20%E2%80%94%20I%27m%20listing%20my%20motorhome%20on%20GoGallivanting%20and%20have%20a%20question.';
  const CALENDLY_URL = 'https://calendly.com/gogallivanting/30min';

  const loadCalendlyScript = () => {
    return new Promise(resolve => {
      if (window.Calendly) {
        resolve();
        return;
      }
      if (document.querySelector('script[data-calendly]')) {
        const existing = document.querySelector('script[data-calendly]');
        existing.addEventListener('load', () => resolve());
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.setAttribute('data-calendly', 'true');
      script.onload = () => resolve();
      document.body.appendChild(script);
    });
  };

  const openCalendly = () => {
    loadCalendlyScript().then(() => {
      if (window.Calendly) {
        window.Calendly.initPopupWidget({ url: CALENDLY_URL });
      }
    });
    setSupportOpen(false);
  };

  const handleWhatsApp = () => {
    window.open(WA_URL, '_blank', 'noopener noreferrer');
    setSupportOpen(false);
  };

  const handleChatClick = () => {
    setChatOpen(true);
    setSupportOpen(false);
  };
  useEffect(() => {
    const handleClickOutside = e => {
      if (supportOpen && supportRef.current && !supportRef.current.contains(e.target)) {
        setSupportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [supportOpen]);
  const [mounted, setMounted] = useState(false);
  const isAdmin = currentUser?.attributes?.profile?.publicData?.isAdmin;
  const isProvider = currentUser?.attributes?.profile?.publicData?.userType === 'owner';
  const pageID = isProvider ? 'how-it-works-owners' : 'how-it-works-renters';

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      chatNotificationCount={props.chatNotificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <>
      <TopbarSearchForm
        className={classNames(css.searchLink, { [css.takeAvailableSpace]: '' })}
        desktopInputRoot={css.topbarSearchWithLeftPadding}
        onSubmit={onSearchSubmit}
        initialValues={initialSearchFormValues}
        appConfig={config}
      />
      <LinkedLogo
        className={css.logoLinkListingPage}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
    </>
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    >
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
    </div>
  );

  const GopilotLink = () => {
    return (
      <NamedLink name="GopilotPage" className={css.topbarLink}>
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.gopilot" />
        </span>
      </NamedLink>
    );
  };
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const GopilotPageMaybe = isAuthenticatedOrJustHydrated && userType ? null : <GopilotLink />;

  // return (
  //   <>
  //   <nav className={classes}>
  //     <div className={css.socialMediaLinks}>
  //       <a href='#'><img src={FB} width={38} /></a>
  //       <a href='#'><img src={INSTA} width={38} /></a>
  //       <a href='#'><img src={SC} width={38} /></a>
  //     </div>

  //     {searchFormMaybe}

  //     {isAdmin ? <DashboardLink/> : null}

  //     <CustomLinksMenu
  //       currentPage={currentPage}
  //       customLinks={customLinks}
  //       intl={intl}
  //       hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated} />

  //     {inboxLinkMaybe}
  //     {profileMenuMaybe}
  //     {signupLinkMaybe}
  //     {loginLinkMaybe}
  //   </nav>
  //   <div>
  //     <div className={css.subNav}>
  //       <span>How it Works</span> <span className={css.spacerNav}>| </span>
  //       <span>List in minutes</span> <span className={css.spacerNav}>|</span>
  //       <span>10% Earlybird Offer</span>
  //     </div>
  //   </div></>
  // )}else{
  return (
    <>
      <nav className={classes}>
        <div className={css.socialMediaLinks} ref={supportRef}>
          <a
            href="https://www.facebook.com/gogallivantinguk/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={FB} width={38} />
          </a>

          <a
            href="https://www.instagram.com/gogallivantinguk/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={INSTA} width={38} />
          </a>
          <a
            href="https://www.tiktok.com/@gogallivantinguk/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={SC} width={38} />
          </a>

          {/* <div className={css.supportContainer}>
            <button
              className={css.supportTrigger}
              onClick={() => setSupportOpen(prev => !prev)}
            >
              <img src={QM} width={42} />

            </button>
            {supportOpen && (
              <div className={css.supportDropdown}>
                <button className={css.supportItem} onClick={handleChatClick}>
                  <FaRobot />
                  <span><FormattedMessage id="TopbarDesktop.supportChat" defaultMessage="Ask GoPilot™" /></span>
                </button>
                <button className={css.supportItem} onClick={openCalendly}>
                  <SiCalendly />
                  <span><FormattedMessage id="TopbarDesktop.supportCalendly" defaultMessage="Book Your GoLive Call" /></span>
                </button>
                <button className={css.supportItem} onClick={handleWhatsApp}>
                  <FaWhatsapp />
                  <span><FormattedMessage id="TopbarDesktop.supportWhatsApp" defaultMessage="WhatsApp" /></span>
                </button>
              </div>
            )}
          </div> */}
        </div>

        {searchFormMaybe}

        {isAdmin ? <DashboardLink /> : null}

        <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
          isProvider={isProvider}
        />

        {GopilotPageMaybe}
        {inboxLinkMaybe}
        {profileMenuMaybe}
        {signupLinkMaybe}
        {loginLinkMaybe}
      </nav>
      <div>
        <div className={css.subNav}>
          <span><NamedLink style={{ textDecoration: 'none', color: 'inherit' }} name="CMSPage" params={{ pageId: pageID }}>How it Works</NamedLink></span> <span className={css.spacerNav}>| </span>
          <span><NamedLink style={{ textDecoration: 'none', color: 'inherit' }} name="EarningsCalculatorPage">Earnings calculator</NamedLink></span>
          <span className={css.spacerNav}>|</span>
          {/* <span>
            <a
              href="https://mailchi.mp/147364420a2c/gogetters-early-access"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              10% Earlybird Offer
            </a>
          </span> */}
          <span><NamedLink style={{ textDecoration: 'none', color: 'inherit' }} name="CMSPage" params={{ pageId: 'meet_the_owner' }}>Why GoGallivanting?</NamedLink></span>
        </div>
      </div>
      <AIChatWidget
        key={isListingPage ? `ctx-${currentListing?.id?.uuid}` : 'general'}
        listingData={isListingPage ? currentListing : null}
        reviews={isListingPage ? listingReviews : []}
        tenantId={process.env.REACT_APP_AYAKAN_AI_CLIENT_ID}
        userId={currentUser?.id?.uuid || null}
        theme="light"
        primaryColor="303b4b"
        mode={isListingPage ? 'context-chat' : 'general-chat'}
        title={isListingPage ? 'Ask GoPilot™' : 'GoPilot™ — Here to help'}
        isOpen={chatOpen}
        onOpenChange={setChatOpen}
      />
    </>
  );
};

export default TopbarDesktop;
